import { apiInstance, gql } from "@/services/httpAdapter";

// Commons Fragments
import { InputValueFragment, LongevityFragment, TopPerformerBonusFragment } from "@PayPlans/api/fragments/commons";

export async function getTechnicianPayPlans(locationId, position) {
	const query = gql`
		query getTechnicianPayPlans($locationId: String!, $position: String!) {
			getTechnicianPayPlans(locationId: $locationId, position: $position) {
				_id
				name
				position
				locked
				available
			}
		}
	`;

	const variables = {
		locationId,
		position,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getTechnicianPayPlans || [];
	} catch (error) {
		console.error(error);
		return [];
	}
}

export async function getTechnicianPayPlan(locationId, payPlanId, position) {
	const query = gql`
		query getTechnicianPayPlan($locationId: String!, $id: String!, $position: String!) {
			getTechnicianPayPlan(locationId: $locationId, id: $id, position: $position) {
				_id
				locationId
				name
				position
				default {
					_id
					defaultValue {
						input
						value
					}
					isGuaranteedDraw
				}
				flaggedHoursBonus {
					_id
					tiers {
						_id
						from {
							...InputValue
						}
						to {
							...InputValue
						}
						commissions {
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commission {
								...InputValue
							}
						}
					}
					applyTiersRetroactively
				}
				nightShiftPay {
					_id
					daysSelected
					offset
					from
					to
					isMultipleDays
					commission {
						input
						value
					}
					prorateByTimeCard
				}
				holidayPay {
					_id
					holidaysSelected {
						localHolidayName
						holidayName
						date
					}
					holidayByType
				}
				retentionPay {
					retention {
						input
						value
					}
					isGuaranteedDraw
				}
				topTechniciansBonus {
					...TopPerformerBonus
				}
				targetedPerformanceBonus {
					_id
					departments {
						tiers {
							_id
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commissions {
								from {
									...InputValue
								}
								to {
									...InputValue
								}
								commission {
									...InputValue
								}
							}
						}
						applyTiersRetroactively
						selectedDepartment
						selectedPayType
						component
					}
					operationCodes {
						tiers {
							_id
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commissions {
								from {
									...InputValue
								}
								to {
									...InputValue
								}
								commission {
									...InputValue
								}
							}
						}
						applyTiersRetroactively
						selectedPayType
						selectedOperationCodes {
							_id
							code
							type
							pay
						}
						selectedTimeLapse
						component
					}
				}
				longevity {
					...Longevity
				}
				locked
				available
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${LongevityFragment}
		${InputValueFragment}
		${TopPerformerBonusFragment}
	`;

	const variables = {
		locationId,
		position,
		id: payPlanId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getTechnicianPayPlan || [];
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function getTechnicianPayPlanMatches(locationId, payPlanId, position) {
	const query = gql`
		query getTechnicianPayPlanMatches($locationId: String!, $id: String!, $position: String!) {
			getTechnicianPayPlanMatches(id: $id, locationId: $locationId, position: $position) {
				locationId
				payPlanId
				fullMatchList
				partialMatchList
			}
		}
	`;

	const variables = {
		id: payPlanId,
		locationId,
		position,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getTechnicianPayPlanMatches;
	} catch (error) {
		console.error(error);
	}

	return {
		locationId,
		payPlanId,
		fullMatchList: [],
		partialMatchList: [],
	};
}

export async function getTechnicianPayPlansSidebarTree(locationId, position, dontGetAdditionalPositions = false) {
	const query = gql`
		query getTechnicianPayPlansSidebarTree($locationId: String!, $position: String!, $dontGetAdditionalPositions: Boolean) {
			getTechnicianPayPlansSidebarTree(
				locationId: $locationId
				position: $position
				dontGetAdditionalPositions: $dontGetAdditionalPositions
			) {
				defaultPayPlans {
					defaultPayPlan {
						_id
						name
						position
						pdfAvailability
					}
					newVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
					preOwnedVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
				}
				available {
					_id
					name
					position
					pdfAvailability
				}
				inProgress {
					_id
					name
					position
					pdfAvailability
				}
				currentlyAssigned {
					_id
					name
					position
					pdfAvailability
				}
				previouslyAssigned {
					_id
					name
					position
					pdfAvailability
				}
			}
		}
	`;

	const variables = {
		locationId,
		position,
		dontGetAdditionalPositions,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getTechnicianPayPlansSidebarTree || [];
	} catch (error) {
		console.error(error);
		return [];
	}
}

export const getTechnicianPayRates = async (locationId, position) => {
	const query = gql`
		query getTechnicianPayRates($locationId: String, $position: String) {
			getTechnicianPayRates(locationId: $locationId, position: $position) {
				rates
			}
		}
	`;

	const variables = {
		locationId,
		position,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getTechnicianPayRates;
	} catch (error) {
		console.error(error);
		return [];
	}
};

export const getTechnicianSecondariesPayRates = async (locationId, position) => {
	const query = gql`
		query getTechnicianPayRates($locationId: String, $position: String) {
			getTechnicianPayRates(locationId: $locationId, position: $position) {
				secondaries
			}
		}
	`;

	const variables = {
		locationId,
		position,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getTechnicianPayRates;
	} catch (error) {
		console.error(error);
		return [];
	}
};
